import { authHandlers } from './auth';

const { fetch: originalFetch } = window;
export const HTTPErrorKind = {
  OK: 200,
  NoContent: 204,
  NotFound: 404,
  Validation: 422,
  Expired: 401,
  AccessDenied: 403,
  Server: 500,
} as const;

type GlobalConfig = {
  onExpire?: () => void;
  baseUrl?: string;
  token?: string;
  excludeTimeZone?: boolean;
  excludeCustomHeaders?: boolean;
  isDebugModeEnabled?: boolean;
};

const DEBUG_HEADER = 'X-Wz-Debug-Mode';
const TIMEZONE_HEADER = 'X-WZ-TIMEZONE';
export const DEBUG_QUERY_STRING = 'debug';
export const TENANT_HEADER = 'x-wz-tenant';

const resolveTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Could not resolve timezone, defaulting to UTC', error);
    return undefined;
  }
};

export const callApi =
  (globalConfig: GlobalConfig) =>
  async <T>(
    input: RequestInfo | URL,
    init?: RequestInit & {
      authless?: boolean;
      asJson?: boolean;
      tenantCode?: string; // temporary solution for wyzCore
      // zodSchema?: ZodSchema;
    },
  ): Promise<T> => {
    const { asJson = true, authless } = init || {};
    const customHeaders: RequestInit['headers'] = {};
    if (!authless) {
      customHeaders['Authorization'] =
        `Bearer ${globalConfig.token || authHandlers().getToken()}`;
    }
    if (!globalConfig.excludeCustomHeaders) {
      if (authHandlers().getTenant()) {
        customHeaders[TENANT_HEADER] = authHandlers().getTenant() as string;
      }
      if (init?.tenantCode) {
        customHeaders[TENANT_HEADER] = init?.tenantCode;
      }
      if (globalConfig.isDebugModeEnabled) {
        customHeaders[DEBUG_HEADER] = 'true';
      }
      const excludeTimeZone = globalConfig.excludeTimeZone ?? false;
      const tz = resolveTimeZone();
      if (tz && !excludeTimeZone) {
        customHeaders[TIMEZONE_HEADER] = tz;
      }
    }
    if (asJson) {
      customHeaders['Content-Type'] = 'application/json';
    }

    const response = await originalFetch(
      globalConfig.baseUrl && typeof input === 'string'
        ? `${globalConfig.baseUrl}${input}`
        : input,
      {
        ...init,
        credentials: 'same-origin',
        headers: {
          ...customHeaders,
          ...init?.headers,
        },
      },
    );
    if (response.status === HTTPErrorKind.Expired) {
      globalConfig.onExpire?.();
      throw new Error('Expired token');
    }
    if (!response.ok) {
      throw await response.json();
    }
    if (response.status === HTTPErrorKind.NoContent) {
      return { data: null, errors: [], message: '' } as T;
    }
    const currentTenant = response.headers.get(TENANT_HEADER);
    if (currentTenant) {
      authHandlers().setTenant(currentTenant);
    }
    const apiResponse = await response.json();
    if (globalConfig.isDebugModeEnabled) {
      // eslint-disable-next-line no-console
      console.log(apiResponse.debug);
    }

    return apiResponse;
  };
